
//  ---------------------------------------------------------------------------

import { sha256 } from '@noble/hashes/sha2.js';
import Exchange from './abstract/bitstamp.js';
import { AccountSuspended, AuthenticationError, BadRequest, ExchangeError, NotSupported, PermissionDenied, InvalidNonce, OrderNotFound, InsufficientFunds, InvalidAddress, InvalidOrder, OnMaintenance, ExchangeNotAvailable } from './base/errors.js';
import { Precise } from './base/Precise.js';
import { TICK_SIZE } from './base/functions/number.js';

;
import type { Balances, Currencies, Currency, Dict, Int, List, Market, Num, OHLCV, Order, OrderBook, OrderSide, OrderType, Str, Strings, Ticker, Tickers, Trade, TradingFeeInterface, TradingFees, Transaction, TransferEntry, int, LedgerEntry, DepositAddress, FundingRateHistory, FundingRate, NullableDict, FeeString, FeeStringInterface, DepositWithdrawFees, Endpoint } from './base/types.js';

//  ---------------------------------------------------------------------------

/**
 * @class bitstamp
 * @augments Exchange
 */
export default class bitstamp extends Exchange {
    override describe (): any {
        return this.deepExtend (super.describe (), {
            'id': 'bitstamp',
            'name': 'Bitstamp',
            'countries': [ 'GB' ],
            // 8000 requests per 10 minutes = 8000 / 600 = 13.33333333 requests per second => 1000ms / 13.33333333 = 75ms between requests on average
            'rateLimit': 75,
            'version': 'v2',
            'userAgent': this.userAgents['chrome'],
            'pro': true,
            'has': {
                'CORS': true,
                'spot': true,
                'margin': false,
                'swap': true,
                'future': false,
                'option': false,
                'addMargin': false,
                'borrowCrossMargin': false,
                'borrowIsolatedMargin': false,
                'borrowMargin': false,
                'cancelAllOrders': true,
                'cancelOrder': true,
                'closeAllPositions': false,
                'closePosition': false,
                'createOrder': true,
                'createOrderWithTakeProfitAndStopLoss': false,
                'createOrderWithTakeProfitAndStopLossWs': false,
                'createReduceOnlyOrder': false,
                'createStopLimitOrder': false,
                'createStopMarketOrder': false,
                'createStopOrder': false,
                'editOrder': true,
                'fetchBalance': true,
                'fetchBorrowInterest': false,
                'fetchBorrowRate': false,
                'fetchBorrowRateHistories': false,
                'fetchBorrowRateHistory': false,
                'fetchBorrowRates': false,
                'fetchBorrowRatesPerSymbol': false,
                'fetchCrossBorrowRate': false,
                'fetchCrossBorrowRates': false,
                'fetchCurrencies': true,
                'fetchDepositAddress': true,
                'fetchDepositAddresses': false,
                'fetchDepositAddressesByNetwork': false,
                'fetchDepositsWithdrawals': true,
                'fetchDepositWithdrawFee': 'emulated',
                'fetchDepositWithdrawFees': true,
                'fetchFundingHistory': false,
                'fetchFundingInterval': false,
                'fetchFundingIntervals': false,
                'fetchFundingRate': true,
                'fetchFundingRateHistory': true,
                'fetchFundingRates': false,
                'fetchGreeks': false,
                'fetchIndexOHLCV': false,
                'fetchIsolatedBorrowRate': false,
                'fetchIsolatedBorrowRates': false,
                'fetchIsolatedPositions': false,
                'fetchLedger': true,
                'fetchLeverage': false,
                'fetchLeverages': false,
                'fetchLeverageTiers': false,
                'fetchLiquidations': false,
                'fetchLongShortRatio': false,
                'fetchLongShortRatioHistory': false,
                'fetchMarginAdjustmentHistory': false,
                'fetchMarginMode': false,
                'fetchMarginModes': false,
                'fetchMarketLeverageTiers': false,
                'fetchMarkets': true,
                'fetchMarkOHLCV': false,
                'fetchMarkPrices': false,
                'fetchMyLiquidations': false,
                'fetchMySettlementHistory': false,
                'fetchMyTrades': true,
                'fetchOHLCV': true,
                'fetchOpenInterest': false,
                'fetchOpenInterestHistory': false,
                'fetchOpenInterests': false,
                'fetchOpenOrders': true,
                'fetchOption': false,
                'fetchOptionChain': false,
                'fetchOrder': true,
                'fetchOrderBook': true,
                'fetchPosition': false,
                'fetchPositionHistory': false,
                'fetchPositionMode': false,
                'fetchPositions': false,
                'fetchPositionsForSymbol': false,
                'fetchPositionsHistory': false,
                'fetchPositionsRisk': false,
                'fetchPremiumIndexOHLCV': false,
                'fetchSettlementHistory': false,
                'fetchTicker': true,
                'fetchTickers': true,
                'fetchTrades': true,
                'fetchTradingFee': true,
                'fetchTradingFees': true,
                'fetchTransactionFees': true,
                'fetchTransactions': 'emulated',
                'fetchVolatilityHistory': false,
                'fetchWithdrawals': true,
                'reduceMargin': false,
                'repayCrossMargin': false,
                'repayIsolatedMargin': false,
                'setLeverage': false,
                'setMargin': false,
                'setMarginMode': false,
                'setPositionMode': false,
                'transfer': true,
                'withdraw': true,
            },
            'urls': {
                'logo': 'https://github.com/user-attachments/assets/d5480572-1fee-43cb-b900-d38c522d0024',
                'api': {
                    'public': 'https://www.bitstamp.net/api',
                    'private': 'https://www.bitstamp.net/api',
                },
                'www': 'https://www.bitstamp.net',
                'doc': 'https://www.bitstamp.net/api',
            },
            'timeframes': {
                '1m': '60',
                '3m': '180',
                '5m': '300',
                '15m': '900',
                '30m': '1800',
                '1h': '3600',
                '2h': '7200',
                '4h': '14400',
                '6h': '21600',
                '12h': '43200',
                '1d': '86400',
                '1w': '259200',
            },
            'requiredCredentials': {
                'apiKey': true,
                'secret': true,
            },
            'api': {
                'public': {
                    'get': {
                        'ohlc/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'order_book/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'ticker/': { 'cost': 1 } as Endpoint<List>,
                        'ticker_hour/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'ticker/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'transactions/{pair}/': { 'cost': 1 } as Endpoint<List>,
                        'trading-pairs-info/': { 'cost': 1 } as Endpoint<List>,
                        'markets/': { 'cost': 1 } as Endpoint<List>,
                        'currencies/': { 'cost': 1 } as Endpoint<List>,
                        'eur_usd/': { 'cost': 1 } as Endpoint<Dict>,
                        'travel_rule/vasps/': { 'cost': 1 } as Endpoint<Dict>,
                        'funding_rate/{market_symbol}/': { 'cost': 1 } as Endpoint<Dict>,
                        'funding_rate_history/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                    },
                },
                'private': {
                    'get': {
                        'travel_rule/contacts/': { 'cost': 1 } as Endpoint<List>,
                        'contacts/{contact_uuid}/': { 'cost': 1 } as Endpoint<Dict>,
                        'earn/subscriptions/': { 'cost': 1 } as Endpoint<List>,
                        'earn/transactions/': { 'cost': 1 } as Endpoint<List>,
                        'trade_history/': { 'cost': 1 } as Endpoint<List>,
                        'trade_history/{pair}': { 'cost': 1 } as Endpoint<List>,
                    },
                    'post': {
                        'account_balances/': { 'cost': 1 } as Endpoint<Dict>,
                        'account_balances/{currency}/': { 'cost': 1 } as Endpoint<Dict>,
                        'balance/': { 'cost': 1 } as Endpoint<Dict>,
                        'balance/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'bch_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'bch_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'user_transactions/': { 'cost': 1 } as Endpoint<List>,
                        'user_transactions/{pair}/': { 'cost': 1 } as Endpoint<List>,
                        'crypto-transactions/': { 'cost': 1 } as Endpoint<Dict>,
                        'open_order': { 'cost': 1 } as Endpoint<Dict>,
                        'open_orders/all/': { 'cost': 1 } as Endpoint<List>,
                        'open_orders/{pair}/': { 'cost': 1 } as Endpoint<List>,
                        'replace_order/': { 'cost': 1 } as Endpoint<Dict>,
                        'order_status/': { 'cost': 1 } as Endpoint<Dict>,
                        'cancel_order/': { 'cost': 1 } as Endpoint<Dict>,
                        'cancel_all_orders/': { 'cost': 1 } as Endpoint<Dict>,
                        'cancel_all_orders/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'buy/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'buy/market/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'buy/instant/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'sell/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'sell/market/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'sell/instant/{pair}/': { 'cost': 1 } as Endpoint<Dict>,
                        'transfer-to-main/': { 'cost': 1 } as Endpoint<Dict>,
                        'transfer-from-main/': { 'cost': 1 } as Endpoint<Dict>,
                        'my_trading_pairs/': { 'cost': 1 } as Endpoint<List>,
                        'fees/trading/': { 'cost': 1 } as Endpoint<List>,
                        'fees/trading/{market_symbol}': { 'cost': 1 } as Endpoint<Dict>,
                        'fees/withdrawal/': { 'cost': 1 } as Endpoint<List>,
                        'fees/withdrawal/{currency}/': { 'cost': 1 } as Endpoint<Dict>,
                        'withdrawal-requests/': { 'cost': 1 } as Endpoint<List>,
                        'withdrawal/open/': { 'cost': 1 } as Endpoint<Dict>,
                        'withdrawal/status/': { 'cost': 1 } as Endpoint<Dict>,
                        'withdrawal/cancel/': { 'cost': 1 } as Endpoint<Dict>,
                        'liquidation_address/new/': { 'cost': 1 } as Endpoint<Dict>,
                        'liquidation_address/info/': { 'cost': 1 } as Endpoint<Dict>,
                        'btc_unconfirmed/': { 'cost': 1 } as Endpoint<Dict>,
                        'websockets_token/': { 'cost': 1 } as Endpoint<Dict>,
                        'revoke_all_api_keys/': { 'cost': 1 } as Endpoint<Dict>,
                        'get_max_order_amount/': { 'cost': 1 } as Endpoint<Dict>,
                        // individual coins
                        'btc_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'btc_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ripple_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ripple_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ltc_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ltc_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'eth_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'eth_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'xrp_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'xrp_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'xlm_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'xlm_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'pax_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'pax_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'link_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'link_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'usdc_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'usdc_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'omg_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'omg_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'dai_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'dai_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'knc_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'knc_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'mkr_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'mkr_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'zrx_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'zrx_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'gusd_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'gusd_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'aave_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'aave_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'bat_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'bat_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'uma_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'uma_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'snx_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'snx_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'uni_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'uni_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'yfi_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'yfi_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'audio_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'audio_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'crv_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'crv_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'algo_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'algo_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'comp_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'comp_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'grt_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'grt_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'usdt_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'usdt_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'eurt_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'eurt_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'matic_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'matic_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'sushi_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'sushi_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'chz_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'chz_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'enj_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'enj_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'alpha_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'alpha_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ftt_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ftt_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'storj_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'storj_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'axs_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'axs_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'sand_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'sand_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'hbar_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'hbar_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'rgt_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'rgt_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'fet_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'fet_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'skl_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'skl_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'cel_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'cel_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'sxp_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'sxp_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ada_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ada_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'slp_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'slp_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ftm_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ftm_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'perp_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'perp_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'dydx_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'dydx_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'gala_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'gala_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'shib_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'shib_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'amp_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'amp_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'sgb_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'sgb_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'avax_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'avax_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'wbtc_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'wbtc_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ctsi_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ctsi_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'cvx_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'cvx_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'imx_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'imx_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'nexo_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'nexo_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ust_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ust_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ant_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ant_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'gods_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'gods_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'rad_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'rad_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'band_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'band_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'inj_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'inj_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'rly_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'rly_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'rndr_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'rndr_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'vega_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'vega_address/': { 'cost': 1 } as Endpoint<Dict>,
                        '1inch_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        '1inch_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ens_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ens_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'mana_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'mana_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'lrc_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'lrc_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ape_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ape_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'mpl_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'mpl_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'euroc_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'euroc_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'sol_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'sol_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'dot_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'dot_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'near_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'near_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'doge_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'doge_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'flr_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'flr_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'dgld_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'dgld_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ldo_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ldo_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'travel_rule/contacts/': { 'cost': 1 } as Endpoint<Dict>,
                        'earn/subscribe/': { 'cost': 1 } as Endpoint<Dict>,
                        'earn/subscriptions/setting/': { 'cost': 1 } as Endpoint<Dict>,
                        'earn/unsubscribe': { 'cost': 1 } as Endpoint<Dict>,
                        'wecan_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'wecan_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'trac_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'trac_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'eurcv_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'eurcv_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'pyusd_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'pyusd_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'lmwr_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'lmwr_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'pepe_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'pepe_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'blur_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'blur_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'vext_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'vext_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'cspr_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'cspr_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'vchf_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'vchf_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'veur_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'veur_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'truf_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'truf_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'wif_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'wif_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'smt_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'smt_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'sui_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'sui_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'jup_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'jup_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'ondo_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'ondo_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'boba_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'boba_address/': { 'cost': 1 } as Endpoint<Dict>,
                        'pyth_withdrawal/': { 'cost': 1 } as Endpoint<Dict>,
                        'pyth_address/': { 'cost': 1 } as Endpoint<Dict>,
                    },
                },
            },
            'fees': {
                'trading': {
                    'tierBased': true,
                    'percentage': true,
                    'taker': this.parseNumber ('0.004'),
                    'maker': this.parseNumber ('0.004'),
                    'tiers': {
                        'taker': [
                            [ this.parseNumber ('0'), this.parseNumber ('0.004') ],
                            [ this.parseNumber ('10000'), this.parseNumber ('0.003') ],
                            [ this.parseNumber ('100000'), this.parseNumber ('0.002') ],
                            [ this.parseNumber ('500000'), this.parseNumber ('0.0018') ],
                            [ this.parseNumber ('1500000'), this.parseNumber ('0.0016') ],
                            [ this.parseNumber ('5000000'), this.parseNumber ('0.0012') ],
                            [ this.parseNumber ('20000000'), this.parseNumber ('0.001') ],
                            [ this.parseNumber ('50000000'), this.parseNumber ('0.0008') ],
                            [ this.parseNumber ('100000000'), this.parseNumber ('0.0006') ],
                            [ this.parseNumber ('250000000'), this.parseNumber ('0.0005') ],
                            [ this.parseNumber ('1000000000'), this.parseNumber ('0.0003') ],
                        ],
                        'maker': [
                            [ this.parseNumber ('0'), this.parseNumber ('0.003') ],
                            [ this.parseNumber ('10000'), this.parseNumber ('0.002') ],
                            [ this.parseNumber ('100000'), this.parseNumber ('0.001') ],
                            [ this.parseNumber ('500000'), this.parseNumber ('0.0008') ],
                            [ this.parseNumber ('1500000'), this.parseNumber ('0.0006') ],
                            [ this.parseNumber ('5000000'), this.parseNumber ('0.0003') ],
                            [ this.parseNumber ('20000000'), this.parseNumber ('0.002') ],
                            [ this.parseNumber ('50000000'), this.parseNumber ('0.0001') ],
                            [ this.parseNumber ('100000000'), this.parseNumber ('0') ],
                            [ this.parseNumber ('250000000'), this.parseNumber ('0') ],
                            [ this.parseNumber ('1000000000'), this.parseNumber ('0') ],
                        ],
                    },
                },
                'funding': {
                    'tierBased': false,
                    'percentage': false,
                    'withdraw': {},
                    'deposit': {
                        'BTC': 0,
                        'BCH': 0,
                        'LTC': 0,
                        'ETH': 0,
                        'XRP': 0,
                        'XLM': 0,
                        'PAX': 0,
                        'USD': 7.5,
                        'EUR': 0,
                    },
                },
            },
            'precisionMode': TICK_SIZE,
            'commonCurrencies': {
                'UST': 'USTC',
            },
            // exchange-specific options
            'options': {
                'mica': true,
                'networksById': {
                    'bitcoin-cash': 'BCH',
                    'bitcoin': 'BTC',
                    'ethereum': 'ERC20',
                    'litecoin': 'LTC',
                    'stellar': 'XLM',
                    'xrpl': 'XRP',
                    'tron': 'TRC20',
                    'algorand': 'ALGO',
                    'flare': 'FLR',
                    'hedera': 'HBAR',
                    'cardana': 'ADA',
                    'songbird': 'FLR',
                    'avalanche-c-chain': 'AVAX',
                    'solana': 'SOL',
                    'polkadot': 'DOT',
                    'near': 'NEAR',
                    'doge': 'DOGE',
                    'sui': 'SUI',
                    'casper': 'CSRP',
                },
            },
            'exceptions': {
                'exact': {
                    'No permission found': PermissionDenied,
                    'API key not found': AuthenticationError,
                    'IP address not allowed': PermissionDenied,
                    'Invalid nonce': InvalidNonce,
                    'Invalid signature': AuthenticationError,
                    'Authentication failed': AuthenticationError,
                    'Missing key, signature and nonce parameters': AuthenticationError,
                    'Wrong API key format': AuthenticationError,
                    'Your account is frozen': PermissionDenied,
                    'Please update your profile with your FATCA information, before using API.': PermissionDenied,
                    'Order not found.': OrderNotFound,
                    "Bitstamp.net is under scheduled maintenance. We'll be back soon.": OnMaintenance, // { "error": "Bitstamp.net is under scheduled maintenance. We'll be back soon." }
                    'Order could not be placed.': ExchangeNotAvailable, // Order could not be placed (perhaps due to internal error or trade halt). Please retry placing order.
                    'Invalid offset.': BadRequest,
                    'Trading is currently unavailable for your account.': AccountSuspended, // {"status": "error", "reason": {"__all__": ["Trading is currently unavailable for your account."]}, "response_code": "403.004"}
                },
                'broad': {
                    'Minimum order size is': InvalidOrder, // Minimum order size is 5.0 EUR.
                    'Price is more than': InvalidOrder,
                    'Check your account balance for details.': InsufficientFunds, // You have only 0.00100000 BTC available. Check your account balance for details.
                    'Ensure this value has at least': InvalidAddress, // Ensure this value has at least 25 characters (it has 4).
                    'Ensure that there are no more than': InvalidOrder, // {"status": "error", "reason": {"amount": ["Ensure that there are no more than 0 decimal places."], "__all__": [""]}}
                },
            },
            'features': {
                'spot': {
                    'sandbox': false,
                    'createOrder': {
                        'marginMode': false,
                        'triggerPrice': false,
                        'triggerPriceType': undefined,
                        'triggerDirection': false,
                        'stopLossPrice': false,
                        'takeProfitPrice': false,
                        'attachedStopLossTakeProfit': undefined,
                        'timeInForce': {
                            'IOC': true,
                            'FOK': true,
                            'PO': true,
                            'GTD': true,
                        },
                        'hedged': false,
                        'trailing': false,
                        'leverage': false,
                        'marketBuyByCost': false,
                        'marketBuyRequiresPrice': false,
                        'selfTradePrevention': false,
                        'iceberg': false,
                    },
                    'createOrders': undefined,
                    'fetchMyTrades': {
                        'marginMode': false,
                        'limit': 1000,
                        'daysBack': undefined,
                        'untilDays': 30,
                        'symbolRequired': false,
                    },
                    'fetchOrder': {
                        'marginMode': false,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': false,
                    },
                    'fetchOpenOrders': {
                        'marginMode': false,
                        'limit': undefined,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': false,
                    },
                    'fetchOrders': undefined,
                    'fetchClosedOrders': undefined,
                    'fetchOHLCV': {
                        'limit': 1000,
                    },
                },
                'swap': {
                    'linear': undefined,
                    'inverse': undefined,
                },
                'future': {
                    'linear': undefined,
                    'inverse': undefined,
                },
            },
        });
    }

    /**
     * @method
     * @name bitstamp#fetchMarkets
     * @description retrieves data on all markets for bitstamp
     * @see https://www.bitstamp.net/api/#tag/Market-info/operation/GetTradingPairsInfo
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} an array of objects representing market data
     */
    override async fetchMarkets (params = {}): Promise<Market[]> {
        const response = await this.fetchMarketsFromCache (params);
        //
        //    [
        //
        //   spot:
        //
        //        {
        //            "name": "BTC/USD",
        //            "market_symbol": "btcusd",
        //            "base_currency": "BTC",
        //            "base_decimals": 8,
        //            "counter_currency": "USD",
        //            "counter_decimals": 0,
        //            "minimum_order_value": "10",
        //            "trading": "Enabled",
        //            "instant_order_counter_decimals": 2,
        //            "instant_and_market_orders": "Enabled",
        //            "description": "Bitcoin / U.S. dollar",
        //            "market_type": "SPOT"
        //        },
        //        ...
        //
        //    perp:
        //
        //         {
        //             "name": "BTC/USD-PERP",
        //             "market_symbol": "btcusd-perp",
        //             "base_currency": "BTC",
        //             "base_decimals": 5,
        //             "counter_currency": "USD",
        //             "counter_decimals": 0,
        //             "minimum_order_value": "10",
        //             "maximum_order_value": "500000.00000000",
        //             "minimum_order_amount": "0.00001000",
        //             "maximum_order_amount": "10.00000000",
        //             "trading": "Enabled",
        //             "instant_order_counter_decimals": 2,
        //             "instant_and_market_orders": "Enabled",
        //             "description": "Bitcoin / U.S. dollar Perpetual",
        //             "market_type": "PERPETUAL",
        //             "underlying_asset": "Kaiko BTC Benchmark Reference Rate",
        //             "payoff_type": "Linear",
        //             "contract_size": "1.00000000",
        //             "isin": "EZHKD4DNKHY3"
        //         }
        //
        const result: List = [];
        for (let i = 0; i < response.length; i++) {
            const market = response[i];
            const [ baseId, quoteId ] = [ this.safeString (market, 'base_currency'), this.safeString (market, 'counter_currency') ];
            const base = this.safeCurrencyCode (baseId);
            const quote = this.safeCurrencyCode (quoteId);
            let settleId: Str = undefined;
            const marketTypeRaw = this.safeString (market, 'market_type');
            let symbol = base + '/' + quote;
            let type: Str = undefined;
            let subType: Str = undefined;
            if (marketTypeRaw === 'SPOT') {
                type = 'spot';
            } else if (marketTypeRaw === 'PERPETUAL') {
                type = 'swap';
                settleId = quoteId;
                symbol = base + '/' + quote + ':' + settleId;
                const payoffType = this.safeString (market, 'payoff_type');
                if (payoffType === 'Linear') {
                    subType = 'linear';
                } else if (payoffType === 'Inverse') {
                    subType = 'inverse';
                }
            }
            const isSpot = (type === 'spot');
            const settle = settleId ? this.safeCurrencyCode (settleId) : undefined;
            result.push ({
                'id': this.safeString (market, 'market_symbol'),
                'symbol': symbol,
                'base': base,
                'quote': quote,
                'settle': settle,
                'baseId': baseId,
                'quoteId': quoteId,
                'settleId': settleId,
                'type': type,
                'subType': subType,
                'spot': isSpot,
                'margin': false,
                'future': false,
                'swap': !isSpot,
                'option': false,
                'active': (this.safeString (market, 'trading') === 'Enabled'),
                'contract': !isSpot,
                'linear': isSpot ? undefined : true,
                'inverse': isSpot ? undefined : false,
                'contractSize': undefined,
                'expiry': undefined,
                'expiryDatetime': undefined,
                'strike': undefined,
                'optionType': undefined,
                'precision': {
                    'amount': this.parseNumber (this.parsePrecision (this.safeString (market, 'base_decimals'))),
                    'price': this.parseNumber (this.parsePrecision (this.safeString (market, 'counter_decimals'))),
                },
                'limits': {
                    'leverage': {
                        'min': undefined,
                        'max': undefined,
                    },
                    'amount': {
                        'min': this.safeNumber (market, 'minimum_order_amount'),
                        'max': this.safeNumber (market, 'maximum_order_amount'),
                    },
                    'price': {
                        'min': undefined,
                        'max': undefined,
                    },
                    'cost': {
                        'min': this.safeNumber (market, 'minimum_order_value'),
                        'max': this.safeNumber (market, 'maximum_order_value'),
                    },
                },
                'created': undefined,
                'info': market,
            });
        }
        return result;
    }

    constructCurrencyObject (id: any, code: any, name: any, precision: any, minCost: any, originalPayload: any) {
        let currencyType = 'crypto';
        const description = this.describe ();
        if (this.isFiat (code)) {
            currencyType = 'fiat';
        }
        const tickSize = this.parseNumber (this.parsePrecision (this.numberToString (precision)));
        return {
            'id': id,
            'code': code,
            'info': originalPayload, // the original payload
            'type': currencyType,
            'name': name,
            'active': true,
            'deposit': undefined,
            'withdraw': undefined,
            'fee': this.safeNumber (description['fees']['funding']['withdraw'], code),
            'precision': tickSize,
            'limits': {
                'amount': {
                    'min': tickSize,
                    'max': undefined,
                },
                'price': {
                    'min': tickSize,
                    'max': undefined,
                },
                'cost': {
                    'min': minCost,
                    'max': undefined,
                },
                'withdraw': {
                    'min': undefined,
                    'max': undefined,
                },
            },
            'networks': {},
        };
    }

    async fetchMarketsFromCache (params = {}) {
        // this method is now redundant
        // currencies are now fetched before markets
        const options = this.safeValue (this.options, 'fetchMarkets', {});
        const timestamp = this.safeInteger (options, 'timestamp');
        const expires = this.safeInteger (options, 'expires', 1000);
        const now = this.milliseconds ();
        if ((timestamp === undefined) || ((now - timestamp) > expires)) {
            const response = await this.publicGetMarkets (params);
            //
            //    [
            //        {
            //            "name": "BTC/USD",
            //            "market_symbol": "btcusd",
            //            "base_currency": "BTC",
            //            "base_decimals": 8,
            //            "counter_currency": "USD",
            //            "counter_decimals": 0,
            //            "minimum_order_value": "10",
            //            "trading": "Enabled",
            //            "instant_order_counter_decimals": 2,
            //            "instant_and_market_orders": "Enabled",
            //            "description": "Bitcoin / U.S. dollar",
            //            "market_type": "SPOT"
            //        },
            //
            this.options['fetchMarkets'] = this.extend (options, {
                'response': response,
                'timestamp': now,
            });
        }
        return this.safeValue (this.options['fetchMarkets'], 'response');
    }

    /**
     * @method
     * @name bitstamp#fetchCurrencies
     * @description fetches all available currencies on an exchange
     * @see https://www.bitstamp.net/api/#tag/Market-info/operation/GetTradingPairsInfo
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} an associative dictionary of currencies
     */
    override async fetchCurrencies (params = {}): Promise<Currencies> {
        const response = await this.fetchMarketsFromCache (params);
        //
        //     [
        //         {
        //             "trading": "Enabled",
        //             "base_decimals": 8,
        //             "url_symbol": "btcusd",
        //             "name": "BTC/USD",
        //             "instant_and_market_orders": "Enabled",
        //             "minimum_order": "20.0 USD",
        //             "counter_decimals": 2,
        //             "description": "Bitcoin / U.S. dollar"
        //         },
        //     ]
        //
        return this.parseCurrencies (response);
    }

    override parseCurrencies (rawCurrencies: any): Currencies {
        // each market row yields two currencies so the accumulation happens
        // in a local dictionary here instead of a temp key inside this.options
        // because the shared scratch key raced between concurrent
        // fetchCurrencies invocations in the multi threaded runtimes
        const result: Dict = {};
        const arr = this.toArray (rawCurrencies);
        for (let i = 0; i < arr.length; i++) {
            const market = arr[i];
            const [ baseId, quoteId ] = [ this.safeString (market, 'base_currency'), this.safeString (market, 'counter_currency') ];
            const base = this.safeCurrencyCode (baseId);
            const quote = this.safeCurrencyCode (quoteId);
            const description = this.safeString (market, 'description');
            if (description === undefined) {
                throw new ExchangeError (this.id + ' parseCurrencies() missing description');
            }
            const [ baseDescription, quoteDescription ] = description.split (' / ');
            const minimumOrder = this.safeString (market, 'minimum_order_value');
            if (minimumOrder === undefined) {
                throw new ExchangeError (this.id + ' parseCurrencies() missing minimumOrder');
            }
            const parts = minimumOrder.split (' ');
            const cost = parts[0];
            if ((base !== undefined) && !(base in result)) {
                const baseDecimals = this.safeInteger (market, 'base_decimals');
                result[base] = this.constructCurrencyObject (baseId, base, baseDescription, baseDecimals, undefined, market);
            }
            if ((quote !== undefined) && !(quote in result)) {
                const counterDecimals = this.safeInteger (market, 'counter_decimals');
                result[quote] = this.constructCurrencyObject (quoteId, quote, quoteDescription, counterDecimals, this.parseNumber (cost), market);
            }
        }
        return result;
    }

    /**
     * @method
     * @name bitstamp#fetchOrderBook
     * @description fetches information on open orders with bid (buy) and ask (sell) prices, volumes and other data
     * @see https://www.bitstamp.net/api/#tag/Order-book/operation/GetOrderBook
     * @param {string} symbol unified symbol of the market to fetch the order book for
     * @param {int} [limit] the maximum amount of order book entries to return
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} an [order book structure]{@link https://docs.ccxt.com/?id=order-book-structure}
     */
    override async fetchOrderBook (symbol: string, limit: Int = undefined, params = {}): Promise<OrderBook> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request: Dict = {
            'pair': market['id'],
        };
        const response = await this.publicGetOrderBookPair (this.extend (request, params));
        //
        //     {
        //         "timestamp": "1583652948",
        //         "microtimestamp": "1583652948955826",
        //         "bids": [
        //             [ "8750.00", "1.33685271" ],
        //             [ "8749.39", "0.07700000" ],
        //             [ "8746.98", "0.07400000" ],
        //         ]
        //         "asks": [
        //             [ "8754.10", "1.51995636" ],
        //             [ "8754.71", "1.40000000" ],
        //             [ "8754.72", "2.50000000" ],
        //         ]
        //     }
        //
        const microtimestamp = this.safeInteger (response, 'microtimestamp');
        if (microtimestamp === undefined) {
            throw new ExchangeError (this.id + ' fetchOrderBook() missing microtimestamp');
        }
        const timestamp = this.parseToInt (microtimestamp / 1000);
        const orderbook = this.parseOrderBook (response, market['symbol'], timestamp);
        orderbook['nonce'] = microtimestamp;
        return orderbook;
    }

    override parseTicker (ticker: Dict, market: Market = undefined): Ticker {
        //
        // {
        //     "timestamp": "1686068944",
        //     "high": "26252",
        //     "last": "26216",
        //     "bid": "26208",
        //     "vwap": "25681",
        //     "volume": "3563.13819902",
        //     "low": "25350",
        //     "ask": "26211",
        //     "open": "25730",
        //     "open_24": "25895",
        //     "percent_change_24": "1.24",
        //     "pair": "BTC/USD"
        // }
        //
        const marketId = this.safeString (ticker, 'pair');
        const symbol = this.safeSymbol (marketId, market);
        const timestamp = this.safeTimestamp (ticker, 'timestamp');
        const vwap = this.safeString (ticker, 'vwap');
        const baseVolume = this.safeString (ticker, 'volume');
        const quoteVolume = Precise.stringMul (baseVolume, vwap);
        const last = this.safeString (ticker, 'last');
        return this.safeTicker ({
            'symbol': symbol,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'high': this.safeString (ticker, 'high'),
            'low': this.safeString (ticker, 'low'),
            'bid': this.safeString (ticker, 'bid'),
            'bidVolume': undefined,
            'ask': this.safeString (ticker, 'ask'),
            'askVolume': undefined,
            'vwap': vwap,
            'open': this.safeString (ticker, 'open'),
            'close': last,
            'last': last,
            'previousClose': undefined,
            'change': undefined,
            'percentage': undefined,
            'average': undefined,
            'baseVolume': baseVolume,
            'quoteVolume': quoteVolume,
            'info': ticker,
        }, market);
    }

    /**
     * @method
     * @name bitstamp#fetchTicker
     * @description fetches a price ticker, a statistical calculation with the information calculated over the past 24 hours for a specific market
     * @see https://www.bitstamp.net/api/#tag/Tickers/operation/GetMarketTicker
     * @param {string} symbol unified symbol of the market to fetch the ticker for
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [ticker structure]{@link https://docs.ccxt.com/?id=ticker-structure}
     */
    override async fetchTicker (symbol: string, params = {}): Promise<Ticker> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request: Dict = {
            'pair': market['id'],
        };
        const ticker = await this.publicGetTickerPair (this.extend (request, params));
        //
        // {
        //     "timestamp": "1686068944",
        //     "high": "26252",
        //     "last": "26216",
        //     "bid": "26208",
        //     "vwap": "25681",
        //     "volume": "3563.13819902",
        //     "low": "25350",
        //     "ask": "26211",
        //     "open": "25730",
        //     "open_24": "25895",
        //     "percent_change_24": "1.24"
        // }
        //
        return this.parseTicker (ticker, market);
    }

    /**
     * @method
     * @name bitstamp#fetchTickers
     * @description fetches price tickers for multiple markets, statistical information calculated over the past 24 hours for each market
     * @see https://www.bitstamp.net/api/#tag/Tickers/operation/GetCurrencyPairTickers
     * @param {string[]|undefined} symbols unified symbols of the markets to fetch the ticker for, all market tickers are returned if not assigned
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a dictionary of [ticker structures]{@link https://docs.ccxt.com/?id=ticker-structure}
     */
    override async fetchTickers (symbols: Strings = undefined, params = {}): Promise<Tickers> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const response = await this.publicGetTicker (params);
        //
        // {
        //     "timestamp": "1686068944",
        //     "high": "26252",
        //     "last": "26216",
        //     "bid": "26208",
        //     "vwap": "25681",
        //     "volume": "3563.13819902",
        //     "low": "25350",
        //     "ask": "26211",
        //     "open": "25730",
        //     "open_24": "25895",
        //     "percent_change_24": "1.24",
        //     "pair": "BTC/USD"
        // }
        //
        return this.parseTickers (response, symbols);
    }

    getCurrencyIdFromTransaction (transaction: any) {
        //
        //     {
        //         "fee": "0.00000000",
        //         "btc_usd": "0.00",
        //         "datetime": XXX,
        //         "usd": 0.0,
        //         "btc": 0.0,
        //         "eth": "0.05000000",
        //         "type": "0",
        //         "id": XXX,
        //         "eur": 0.0
        //     }
        //
        const currencyId = this.safeStringLower (transaction, 'currency');
        if (currencyId !== undefined) {
            return currencyId;
        }
        transaction = this.omit (transaction, [
            'fee',
            'price',
            'datetime',
            'type',
            'status',
            'id',
        ]);
        const ids = Object.keys (transaction);
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            if (id.indexOf ('_') < 0) {
                const value = this.safeInteger (transaction, id);
                if ((value !== undefined) && (value !== 0)) {
                    return id;
                }
            }
        }
        return undefined;
    }

    getMarketFromTrade (trade: any) {
        trade = this.omit (trade, [
            'fee',
            'price',
            'datetime',
            'tid',
            'type',
            'order_id',
            'side',
        ]);
        const currencyIds = Object.keys (trade);
        const numCurrencyIds = currencyIds.length;
        if (numCurrencyIds > 2) {
            throw new ExchangeError (this.id + ' getMarketFromTrade() too many keys: ' + this.json (currencyIds) + ' in the trade: ' + this.json (trade));
        }
        if (numCurrencyIds === 2) {
            let marketId = currencyIds[0] + currencyIds[1];
            if ((this.markets_by_id !== undefined) && (marketId in this.markets_by_id)) {
                return this.safeMarket (marketId);
            }
            marketId = currencyIds[1] + currencyIds[0];
            if ((this.markets_by_id !== undefined) && (marketId in this.markets_by_id)) {
                return this.safeMarket (marketId);
            }
        }
        return undefined;
    }

    override parseTrade (trade: Dict, market: Market = undefined): Trade {
        //
        // fetchTrades (public)
        //
        //      {
        //          "date": "1637845199",
        //          "tid": "209895701",
        //          "amount": "0.00500000",
        //          "type": "0",             // Transaction type: 0 - buy; 1 - sell
        //          "price": "4451.25"
        //      }
        //
        // fetchMyTrades, trades returned within fetchOrder (private)
        //
        //      {
        //          "fee": "0.11128",
        //          "eth_usdt":  4451.25,
        //          "datetime": "2021-11-25 12:59:59.322000",
        //          "usdt": "-22.26",
        //          "order_id":  1429545880227846,
        //          "usd":  0,
        //          "btc":  0,
        //          "eth": "0.00500000",
        //          "type": "2",                    // Transaction type: 0 - deposit; 1 - withdrawal; 2 - market trade; 14 - sub account transfer; 25 - credited with staked assets; 26 - sent assets to staking; 27 - staking reward; 32 - referral reward; 35 - inter account transfer.
        //          "id":  209895701,
        //          "eur":  0
        //      }
        //
        // from fetchOrder (private)
        //
        //      {
        //          "fee": "0.11128",
        //          "price": "4451.25000000",
        //          "datetime": "2021-11-25 12:59:59.322000",
        //          "usdt": "22.25625000",
        //          "tid": 209895701,
        //          "eth": "0.00500000",
        //          "type": 2                       // Transaction type: 0 - deposit; 1 - withdrawal; 2 - market trade
        //      }
        //
        const id = this.safeString2 (trade, 'id', 'tid');
        let symbol: Str = undefined;
        let side: Str = undefined;
        let priceString = this.safeString (trade, 'price');
        let amountString = this.safeString (trade, 'amount');
        const orderId = this.safeString (trade, 'order_id');
        const type = undefined;
        let costString = this.safeString (trade, 'cost');
        let rawMarketId: Str = undefined;
        if (market === undefined) {
            const keys = Object.keys (trade);
            for (let i = 0; i < keys.length; i++) {
                const currentKey = keys[i];
                if (currentKey !== 'order_id' && currentKey.indexOf ('_') >= 0) {
                    rawMarketId = currentKey;
                    market = this.safeMarket (rawMarketId, market, '_');
                }
            }
        }
        // if the market is still not defined
        // try to deduce it from used keys
        if (market === undefined) {
            market = this.getMarketFromTrade (trade);
        }
        const feeCostString = this.safeString (trade, 'fee');
        const feeCurrency = this.safeString (market, 'quote');
        const priceId = (rawMarketId !== undefined) ? rawMarketId : this.safeString (market, 'id');
        priceString = this.safeString (trade, priceId, priceString);
        amountString = this.safeString (trade, this.safeString (market, 'baseId'), amountString);
        costString = this.safeString (trade, this.safeString (market, 'quoteId'), costString);
        // this endpoint is not aligned with "markets" endpoint
        const baseIdLower = this.safeStringLower (market, 'baseId');
        const quoteIdLower = this.safeStringLower (market, 'quoteId');
        const dashedIdLower = baseIdLower + '_' + quoteIdLower;
        if (priceString === undefined) {
            priceString = this.safeString (trade, dashedIdLower);
        }
        if (amountString === undefined) {
            amountString = this.safeString (trade, baseIdLower);
        }
        if (costString === undefined) {
            costString = this.safeString (trade, quoteIdLower);
        }
        symbol = this.safeString (market, 'symbol');
        const datetimeString = this.safeString2 (trade, 'date', 'datetime');
        let timestamp: Int = undefined;
        if (datetimeString !== undefined) {
            if (datetimeString.indexOf (' ') >= 0) {
                // iso8601
                timestamp = this.parse8601 (datetimeString);
            } else {
                // string unix epoch in seconds
                timestamp = parseInt (datetimeString);
                timestamp = timestamp * 1000;
            }
        }
        // if it is a private trade
        if ('id' in trade) {
            if (amountString !== undefined) {
                const isAmountNeg = Precise.stringLt (amountString, '0');
                if (isAmountNeg) {
                    side = 'sell';
                    amountString = Precise.stringNeg (amountString);
                } else {
                    side = 'buy';
                }
            }
        } else {
            side = this.safeString (trade, 'type');
            if (side === '1') {
                side = 'sell';
            } else if (side === '0') {
                side = 'buy';
            } else {
                side = undefined;
            }
        }
        if (costString !== undefined) {
            costString = Precise.stringAbs (costString);
        }
        let fee: FeeString = undefined;
        if (feeCostString !== undefined) {
            fee = {
                'cost': feeCostString,
                'currency': feeCurrency,
            };
        }
        return this.safeTrade ({
            'id': id,
            'info': trade,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'symbol': symbol,
            'order': orderId,
            'type': type,
            'side': side,
            'takerOrMaker': undefined,
            'price': priceString,
            'amount': amountString,
            'cost': costString,
            'fee': fee,
        }, market);
    }

    /**
     * @method
     * @name bitstamp#fetchTrades
     * @description get the list of most recent trades for a particular symbol
     * @see https://www.bitstamp.net/api/#tag/Transactions-public/operation/GetTransactions
     * @param {string} symbol unified symbol of the market to fetch trades for
     * @param {int} [since] timestamp in ms of the earliest trade to fetch
     * @param {int} [limit] the maximum amount of trades to fetch
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {Trade[]} a list of [trade structures]{@link https://docs.ccxt.com/?id=public-trades}
     */
    override async fetchTrades (symbol: string, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Trade[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request: Dict = {
            'pair': market['id'],
            'time': 'hour',
        };
        const response = await this.publicGetTransactionsPair (this.extend (request, params));
        //
        //     [
        //         {
        //             "date": "1551814435",
        //             "tid": "83581898",
        //             "price": "0.03532850",
        //             "type": "1",
        //             "amount": "0.85945907"
        //         },
        //         {
        //             "date": "1551814434",
        //             "tid": "83581896",
        //             "price": "0.03532851",
        //             "type": "1",
        //             "amount": "11.34130961"
        //         },
        //     ]
        //
        return this.parseTrades (response, market, since, limit);
    }

    override parseOHLCV (ohlcv: any, market: Market = undefined): OHLCV {
        //
        //     {
        //         "high": "9064.77",
        //         "timestamp": "1593961440",
        //         "volume": "18.49436608",
        //         "low": "9040.87",
        //         "close": "9064.77",
        //         "open": "9040.87"
        //     }
        //
        return [
            this.safeTimestamp (ohlcv, 'timestamp'),
            this.safeNumber (ohlcv, 'open'),
            this.safeNumber (ohlcv, 'high'),
            this.safeNumber (ohlcv, 'low'),
            this.safeNumber (ohlcv, 'close'),
            this.safeNumber (ohlcv, 'volume'),
        ];
    }

    /**
     * @method
     * @name bitstamp#fetchOHLCV
     * @description fetches historical candlestick data containing the open, high, low, and close price, and the volume of a market
     * @see https://www.bitstamp.net/api/#tag/Market-info/operation/GetOHLCData
     * @param {string} symbol unified symbol of the market to fetch OHLCV data for
     * @param {string} timeframe the length of time each candle represents
     * @param {int} [since] timestamp in ms of the earliest candle to fetch
     * @param {int} [limit] the maximum amount of candles to fetch
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {int[][]} A list of candles ordered as timestamp, open, high, low, close, volume
     */
    override async fetchOHLCV (symbol: string, timeframe: string = '1m', since: Int = undefined, limit: Int = undefined, params = {}): Promise<OHLCV[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request: Dict = {
            'pair': market['id'],
            'step': this.safeString (this.timeframes, timeframe, timeframe),
        };
        const duration = this.parseTimeframe (timeframe);
        if (limit === undefined) {
            if (since === undefined) {
                request['limit'] = 1000; // we need to specify an allowed amount of `limit` if no `since` is set and there is no default limit by exchange
            } else {
                limit = 1000;
                const start = this.parseToInt (since / 1000);
                request['start'] = start;
                request['end'] = this.sum (start, duration * (limit - 1));
                request['limit'] = limit;
            }
        } else {
            if (since !== undefined) {
                const start = this.parseToInt (since / 1000);
                request['start'] = start;
                request['end'] = this.sum (start, duration * (limit - 1));
            }
            request['limit'] = Math.min (limit, 1000); // min 1, max 1000
        }
        const response = await this.publicGetOhlcPair (this.extend (request, params));
        //
        //     {
        //         "data": {
        //             "pair": "BTC/USD",
        //             "ohlc": [
        //                 {"high": "9064.77", "timestamp": "1593961440", "volume": "18.49436608", "low": "9040.87", "close": "9064.77", "open": "9040.87"},
        //                 {"high": "9071.59", "timestamp": "1593961500", "volume": "3.48631711", "low": "9058.76", "close": "9061.07", "open": "9064.66"},
        //                 {"high": "9067.33", "timestamp": "1593961560", "volume": "0.04142833", "low": "9061.94", "close": "9061.94", "open": "9067.33"},
        //             ],
        //         }
        //     }
        //
        const data = this.safeValue (response, 'data', {});
        const ohlc = this.safeList (data, 'ohlc', []);
        return this.parseOHLCVs (ohlc, market, timeframe, since, limit);
    }

    override parseBalance (response: any): Balances {
        const finalResponse = response; // java req
        const result: Dict = {
            'info': finalResponse,
            'timestamp': undefined,
            'datetime': undefined,
        };
        if (response === undefined) {
            response = [];
        }
        for (let i = 0; i < response.length; i++) {
            const currencyBalance = response[i];
            const currencyId = this.safeString (currencyBalance, 'currency');
            const currencyCode = this.safeCurrencyCode (currencyId);
            const account = this.account ();
            account['free'] = this.safeString (currencyBalance, 'available');
            account['used'] = this.safeString (currencyBalance, 'reserved');
            account['total'] = this.safeString (currencyBalance, 'total');
            if (currencyCode !== undefined) {
                result[currencyCode] = account;
            }
        }
        return this.safeBalance (result);
    }

    /**
     * @method
     * @name bitstamp#fetchBalance
     * @description query for balance and get the amount of funds available for trading or funds locked in orders
     * @see https://www.bitstamp.net/api/#tag/Account-balances/operation/GetAccountBalances
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [balance structure]{@link https://docs.ccxt.com/?id=balance-structure}
     */
    override async fetchBalance (params = {}): Promise<Balances> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const response = await this.privatePostAccountBalances (params);
        //
        //     [
        //         {
        //             "currency": "usdt",
        //             "total": "7.00000",
        //             "available": "7.00000",
        //             "reserved": "0.00000"
        //         },
        //         ...
        //     ]
        //
        return this.parseBalance (response);
    }

    /**
     * @method
     * @name bitstamp#fetchTradingFee
     * @description fetch the trading fees for a market
     * @see https://www.bitstamp.net/api/#tag/Fees/operation/GetTradingFeesForCurrency
     * @param {string} symbol unified market symbol
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [fee structure]{@link https://docs.ccxt.com/?id=fee-structure}
     */
    override async fetchTradingFee (symbol: string, params = {}): Promise<TradingFeeInterface> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request: Dict = {
            'market_symbol': market['id'],
        };
        const response = await this.privatePostFeesTrading (this.extend (request, params));
        //
        //     [
        //         {
        //             "currency_pair": "btcusd",
        //             "fees":
        //                 {
        //                     "maker": "0.15000",
        //                     "taker": "0.16000"
        //                 },
        //             "market": "btcusd"
        //         }
        //         ...
        //     ]
        //
        const tradingFeesByMarketId = this.indexBy (response, 'currency_pair');
        let tradingFee = this.safeDict (tradingFeesByMarketId, market['id']);
        if (tradingFee === undefined) {
            tradingFee = {};
        }
        return this.parseTradingFee (tradingFee, market);
    }

    parseTradingFee (fee: Dict, market: Market = undefined): TradingFeeInterface {
        const marketId = this.safeString (fee, 'market');
        const fees = this.safeDict (fee, 'fees', {});
        return {
            'info': fee,
            'symbol': this.safeSymbol (marketId, market),
            'maker': this.safeNumber (fees, 'maker'),
            'taker': this.safeNumber (fees, 'taker'),
            'percentage': undefined,
            'tierBased': undefined,
        };
    }

    parseTradingFees (fees: any) {
        const result: Dict = { 'info': fees };
        for (let i = 0; i < fees.length; i++) {
            const fee = this.parseTradingFee (fees[i]);
            const symbol = fee['symbol'];
            if (symbol !== undefined) {
                result[symbol] = fee;
            }
        }
        return result;
    }

    /**
     * @method
     * @name bitstamp#fetchTradingFees
     * @description fetch the trading fees for multiple markets
     * @see https://www.bitstamp.net/api/#tag/Fees/operation/GetAllTradingFees
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a dictionary of [fee structures]{@link https://docs.ccxt.com/?id=fee-structure} indexed by market symbols
     */
    override async fetchTradingFees (params = {}): Promise<TradingFees> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const response = await this.privatePostFeesTrading (params);
        //
        //     [
        //         {
        //             "currency_pair": "btcusd",
        //             "fees":
        //                 {
        //                     "maker": "0.15000",
        //                     "taker": "0.16000"
        //                 },
        //             "market": "btcusd"
        //         }
        //         ...
        //     ]
        //
        return this.parseTradingFees (response);
    }

    /**
     * @method
     * @name bitstamp#fetchTransactionFees
     * @deprecated
     * @description please use fetchDepositWithdrawFees instead
     * @see https://www.bitstamp.net/api/#tag/Fees
     * @param {string[]|undefined} codes list of unified currency codes
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [fee structures]{@link https://docs.ccxt.com/?id=fee-structure}
     */
    override async fetchTransactionFees (codes: Strings = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const response = await this.privatePostFeesWithdrawal (params);
        //
        //     [
        //         {
        //             "currency": "btc",
        //             "fee": "0.00015000",
        //             "network": "bitcoin"
        //         }
        //         ...
        //     ]
        //
        return this.parseTransactionFees (response);
    }

    parseTransactionFees (response: any, codes: Strings = undefined) {
        const result: Dict = {};
        const currencies = this.indexBy (response, 'currency');
        const ids = Object.keys (currencies);
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const fees = this.safeValue (response, i, {});
            const code = this.safeCurrencyCode (id);
            if ((codes !== undefined) && !this.inArray (code, codes)) {
                continue;
            }
            if (code !== undefined) {
                result[code] = {
                    'withdraw_fee': this.safeNumber (fees, 'fee'),
                    'deposit': {},
                    'info': this.safeDict (currencies, id),
                };
            }
        }
        return result;
    }

    /**
     * @method
     * @name bitstamp#fetchDepositWithdrawFees
     * @description fetch deposit and withdraw fees
     * @see https://www.bitstamp.net/api/#tag/Fees/operation/GetAllWithdrawalFees
     * @param {string[]|undefined} codes list of unified currency codes
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [fee structures]{@link https://docs.ccxt.com/?id=fee-structure}
     */
    override async fetchDepositWithdrawFees (codes: Strings = undefined, params = {}): Promise<DepositWithdrawFees> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const response = await this.privatePostFeesWithdrawal (params);
        //
        //     [
        //         {
        //             "currency": "btc",
        //             "fee": "0.00015000",
        //             "network": "bitcoin"
        //         }
        //         ...
        //     ]
        //
        const responseByCurrencyId = this.groupBy (response, 'currency');
        return this.parseDepositWithdrawFees (responseByCurrencyId, codes);
    }

    override parseDepositWithdrawFee (fee: any, currency: Currency = undefined) {
        const result = this.depositWithdrawFee (fee);
        const code = this.safeString (currency, 'code');
        for (let j = 0; j < fee.length; j++) {
            const networkEntry = fee[j];
            const networkId = this.safeString (networkEntry, 'network');
            const networkCode = this.networkIdToCode (networkId, code);
            const withdrawFee = this.safeNumber (networkEntry, 'fee');
            result['withdraw'] = {
                'fee': withdrawFee,
                'percentage': undefined,
            };
            if (networkCode !== undefined) {
                result['networks'][networkCode] = {
                    'withdraw': {
                        'fee': withdrawFee,
                        'percentage': undefined,
                    },
                    'deposit': {
                        'fee': undefined,
                        'percentage': undefined,
                    },
                };
            }
        }
        return result;
    }

    /**
     * @method
     * @name bitstamp#createOrder
     * @description create a trade order
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/OpenInstantBuyOrder
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/OpenMarketBuyOrder
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/OpenLimitBuyOrder
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/OpenInstantSellOrder
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/OpenMarketSellOrder
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/OpenLimitSellOrder
     * @param {string} symbol unified symbol of the market to create an order in
     * @param {string} type 'market' or 'limit'
     * @param {string} side 'buy' or 'sell'
     * @param {float} amount how much of currency you want to trade in units of base currency
     * @param {float} [price] the price at which the order is to be fulfilled, in units of the quote currency, ignored in market orders
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} an [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async createOrder (symbol: string, type: OrderType, side: OrderSide, amount: number, price: Num = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request: Dict = {
            'pair': market['id'],
            'amount': this.amountToPrecision (symbol, amount),
        };
        const clientOrderId = this.safeString2 (params, 'client_order_id', 'clientOrderId');
        if (clientOrderId !== undefined) {
            request['client_order_id'] = clientOrderId;
            params = this.omit (params, [ 'clientOrderId' ]);
        }
        let response: NullableDict = undefined;
        const capitalizedSide = this.capitalize (side);
        if (type === 'market') {
            if (capitalizedSide === 'Buy') {
                response = await this.privatePostBuyMarketPair (this.extend (request, params));
            } else {
                response = await this.privatePostSellMarketPair (this.extend (request, params));
            }
        } else if (type === 'instant') {
            if (capitalizedSide === 'Buy') {
                response = await this.privatePostBuyInstantPair (this.extend (request, params));
            } else {
                response = await this.privatePostSellInstantPair (this.extend (request, params));
            }
        } else {
            request['price'] = this.priceToPrecision (symbol, price);
            if (capitalizedSide === 'Buy') {
                response = await this.privatePostBuyPair (this.extend (request, params));
            } else {
                response = await this.privatePostSellPair (this.extend (request, params));
            }
        }
        const orderResponse = (response === undefined) ? {} : response;
        const order = this.parseOrder (orderResponse, market);
        order['type'] = type;
        return order;
    }

    /**
     * @method
     * @name bitstamp#editOrder
     * @description edit a trade order
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/ReplaceOrder
     * @param {string} id order id
     * @param {string} [symbol] unified symbol of the market to create an order in
     * @param {string} [type] 'market', 'limit' or 'stop_limit'
     * @param {string} [side] 'buy' or 'sell'
     * @param {float} [amount] how much of the currency you want to trade in units of the base currency
     * @param {float} [price] the price for the order, in units of the quote currency, ignored in market orders
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.triggerPrice] the price to trigger a stop order
     * @param {string} [params.timeInForce] for crypto trading either 'gtc' or 'ioc' can be used
     * @param {string} [params.clientOrderId] a unique identifier for the order, automatically generated if not sent
     * @returns {object} an [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async editOrder (id: string, symbol: string, type: OrderType, side: OrderSide, amount: Num = undefined, price: Num = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request: Dict = {
            'amount': this.amountToPrecision (symbol, amount),
            'price': this.priceToPrecision (symbol, price),
        };
        const clientOrderId = this.safeString2 (params, 'client_order_id', 'clientOrderId');
        if (clientOrderId !== undefined) {
            request['client_order_id'] = clientOrderId;
            params = this.omit (params, [ 'clientOrderId' ]);
        } else {
            request['id'] = id;
        }
        const response = await this.privatePostReplaceOrder (this.extend (request, params));
        const order = this.parseOrder (response, market);
        order['type'] = type;
        return order;
    }

    /**
     * @method
     * @name bitstamp#cancelOrder
     * @description cancels an open order
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/CancelOrder
     * @param {string} id order id
     * @param {string} symbol unified symbol of the market the order was made in
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} An [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async cancelOrder (id: string, symbol: Str = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const request: Dict = {
            'id': id,
        };
        const response = await this.privatePostCancelOrder (this.extend (request, params));
        //
        //    {
        //        "id": 1453282316578816,
        //        "amount": "0.02035278",
        //        "price": "2100.45",
        //        "type": 0,
        //        "market": "BTC/USD"
        //    }
        //
        return this.parseOrder (response);
    }

    /**
     * @method
     * @name bitstamp#cancelAllOrders
     * @description cancel all open orders
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/CancelAllOrders
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/CancelOrdersForMarket
     * @param {string} [symbol] unified market symbol, only orders in the market of this symbol are cancelled when symbol is not undefined
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async cancelAllOrders (symbol: Str = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let market: Market = undefined;
        const request: Dict = {};
        let response: NullableDict = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
            request['pair'] = market['id'];
            response = await this.privatePostCancelAllOrdersPair (this.extend (request, params));
        } else {
            response = await this.privatePostCancelAllOrders (this.extend (request, params));
        }
        //
        //    {
        //        "canceled": [
        //            {
        //                "id": 1453282316578816,
        //                "amount": "0.02035278",
        //                "price": "2100.45",
        //                "type": 0,
        //                "currency_pair": "BTC/USD",
        //                "market": "BTC/USD"
        //            }
        //        ],
        //        "success": true
        //    }
        //
        const canceled = this.safeList (response, 'canceled');
        return this.parseOrders (canceled);
    }

    parseOrderStatus (status: Str) {
        const statuses: Dict = {
            'In Queue': 'open',
            'Open': 'open',
            'Finished': 'closed',
            'Canceled': 'canceled',
            'Cancel pending': 'canceling',
        };
        return this.safeString (statuses, status, status);
    }

    override async fetchOrderStatus (id: string, symbol: Str = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const clientOrderId = this.safeValue2 (params, 'client_order_id', 'clientOrderId');
        const request: Dict = {};
        if (clientOrderId !== undefined) {
            request['client_order_id'] = clientOrderId;
            params = this.omit (params, [ 'client_order_id', 'clientOrderId' ]);
        } else {
            request['id'] = id;
        }
        const response = await this.privatePostOrderStatus (this.extend (request, params));
        return this.parseOrderStatus (this.safeString (response, 'status'));
    }

    /**
     * @method
     * @name bitstamp#fetchOrder
     * @description fetches information on an order made by the user
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/GetOrderStatus
     * @param {string} id the order id
     * @param {string} symbol unified symbol of the market the order was made in
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} An [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async fetchOrder (id: string, symbol: Str = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let market: Market = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
        }
        const clientOrderId = this.safeValue2 (params, 'client_order_id', 'clientOrderId');
        const request: Dict = {};
        if (clientOrderId !== undefined) {
            request['client_order_id'] = clientOrderId;
            params = this.omit (params, [ 'client_order_id', 'clientOrderId' ]);
        } else {
            request['id'] = id;
        }
        const response = await this.privatePostOrderStatus (this.extend (request, params));
        //
        //      {
        //          "status": "Finished",
        //          "id": 1429545880227846,
        //          "amount_remaining": "0.00000000",
        //          "transactions": [
        //              {
        //                  "fee": "0.11128",
        //                  "price": "4451.25000000",
        //                  "datetime": "2021-11-25 12:59:59.322000",
        //                  "usdt": "22.25625000",
        //                  "tid": 209895701,
        //                  "eth": "0.00500000",
        //                  "type": 2
        //              }
        //         ]
        //     }
        //
        return this.parseOrder (response, market);
    }

    /**
     * @method
     * @name bitstamp#fetchMyTrades
     * @description fetch all trades made by the user
     * @see https://www.bitstamp.net/api/#tag/Transactions-private/operation/GetUserTransactions
     * @see https://www.bitstamp.net/api/#tag/Transactions-private/operation/GetUserTransactionsForMarket
     * @param {string} symbol unified market symbol
     * @param {int} [since] the earliest time in ms to fetch trades for
     * @param {int} [limit] the maximum number of trades structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {Trade[]} a list of [trade structures]{@link https://docs.ccxt.com/?id=trade-structure}
     */
    override async fetchMyTrades (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const request: Dict = {};
        let method = 'privatePostUserTransactions';
        let market: Market = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
            request['pair'] = market['id'];
            method += 'Pair';
        }
        if (limit !== undefined) {
            request['limit'] = limit;
        }
        const response = await this[method] (this.extend (request, params));
        const result = this.filterBy (response, 'type', '2');
        return this.parseTrades (result, market, since, limit);
    }

    /**
     * @method
     * @name bitstamp#fetchFundingRateHistory
     * @description fetches historical funding rate prices
     * @see https://www.bitstamp.net/api/#tag/Market-info/operation/GetFundingRateHistory
     * @param {string} symbol unified symbol of the market to fetch the funding rate history for
     * @param {int} [since] timestamp in ms of the earliest funding rate to fetch
     * @param {int} [limit] the maximum amount of [funding rate structures]{@link https://docs.ccxt.com/?id=funding-rate-history-structure} to fetch
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.until] timestamp in ms of the latest funding rate
     * @param {boolean} [params.paginate] default false, when true will automatically paginate by calling this endpoint multiple times. See in the docs all the [available parameters](https://github.com/ccxt/ccxt/wiki/Manual#pagination-params)
     * @param {string} [params.subType] "linear" or "inverse"
     * @returns {object[]} a list of [funding rate structures]{@link https://docs.ccxt.com/?id=funding-rate-history-structure}
     */
    override async fetchFundingRateHistory (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<FundingRateHistory[]> {
        let paginate = false;
        [ paginate, params ] = this.handleOptionAndParams (params, 'fetchFundingRateHistory', 'paginate');
        if (paginate) {
            return await this.fetchPaginatedCallDeterministic ('fetchFundingRateHistory', symbol, since, limit, '8h', params) as FundingRateHistory[];
        }
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let request: Dict = {};
        let market: Market = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
            request['pair'] = market['id'];
        }
        if (since !== undefined) {
            request['since_timestamp'] = Math.round (since / 1000);
        }
        [ request, params ] = this.handleUntilOption ('until_timestamp', request, params, 0.001);
        if (limit !== undefined) {
            request['limit'] = limit;
        }
        const response = await this.publicGetFundingRateHistoryPair (this.extend (request, params));
        //
        //     {
        //         "market": "BTC/USD-PERP",
        //         "funding_rate_history": [
        //             {
        //                 "funding_rate": "0.0024",
        //                 "timestamp": "1644406050"
        //             }
        //         ]
        //     }
        //
        const values = this.safeValue (response, 'funding_rate_history', []);
        return this.parseFundingRateHistories (values, market, since, limit) as FundingRateHistory[];
    }

    override parseFundingRateHistory (contract: any, market: Market = undefined) {
        //
        //     {
        //         "funding_rate": "0.0024",
        //         "timestamp": "1644406050"
        //     }
        //
        const timestamp = this.safeIntegerProduct (contract, 'timestamp', 0.001);
        return {
            'info': contract,
            'symbol': undefined,
            'fundingRate': this.safeNumber (contract, 'funding_rate'),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
        };
    }

    /**
     * @method
     * @name bitstamp#fetchDepositsWithdrawals
     * @description fetch history of deposits and withdrawals
     * @see https://www.bitstamp.net/api/#tag/Transactions-private/operation/GetUserTransactions
     * @param {string} [code] unified currency code for the currency of the deposit/withdrawals, default is undefined
     * @param {int} [since] timestamp in ms of the earliest deposit/withdrawal, default is undefined
     * @param {int} [limit] max number of deposit/withdrawals to return, default is undefined
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a list of [transaction structure]{@link https://docs.ccxt.com/?id=transaction-structure}
     */
    override async fetchDepositsWithdrawals (code: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Transaction[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const request: Dict = {};
        if (limit !== undefined) {
            request['limit'] = limit;
        }
        const response = await this.privatePostUserTransactions (this.extend (request, params));
        //
        //     [
        //         {
        //             "fee": "0.00000000",
        //             "btc_usd": "0.00",
        //             "id": 1234567894,
        //             "usd": 0,
        //             "btc": 0,
        //             "datetime": "2018-09-08 09:00:31",
        //             "type": "1",
        //             "xrp": "-20.00000000",
        //             "eur": 0,
        //         },
        //         {
        //             "fee": "0.00000000",
        //             "btc_usd": "0.00",
        //             "id": 1134567891,
        //             "usd": 0,
        //             "btc": 0,
        //             "datetime": "2018-09-07 18:47:52",
        //             "type": "0",
        //             "xrp": "20.00000000",
        //             "eur": 0,
        //         },
        //     ]
        //
        let currency: Currency = undefined;
        if (code !== undefined) {
            currency = this.currency (code);
        }
        const transactions = this.filterByArray (response, 'type', [ '0', '1' ], false);
        return this.parseTransactions (transactions, currency, since, limit);
    }

    /**
     * @method
     * @name bitstamp#fetchWithdrawals
     * @description fetch all withdrawals made from an account
     * @see https://www.bitstamp.net/api/#tag/Withdrawals/operation/GetWithdrawalRequests
     * @param {string} code unified currency code
     * @param {int} [since] the earliest time in ms to fetch withdrawals for
     * @param {int} [limit] the maximum number of withdrawals structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [transaction structures]{@link https://docs.ccxt.com/?id=transaction-structure}
     */
    override async fetchWithdrawals (code: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Transaction[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const request: Dict = {};
        if (since !== undefined) {
            request['timedelta'] = this.milliseconds () - since;
        } else {
            request['timedelta'] = 50000000; // use max bitstamp approved value
        }
        const response = await this.privatePostWithdrawalRequests (this.extend (request, params));
        //
        //     [
        //         {
        //             "status": 2,
        //             "datetime": "2018-10-17 10:58:13",
        //             "currency": "BTC",
        //             "amount": "0.29669259",
        //             "address": "aaaaa",
        //             "type": 1,
        //             "id": 111111,
        //             "transaction_id": "xxxx",
        //         },
        //         {
        //             "status": 2,
        //             "datetime": "2018-10-17 10:55:17",
        //             "currency": "ETH",
        //             "amount": "1.11010664",
        //             "address": "aaaa",
        //             "type": 16,
        //             "id": 222222,
        //             "transaction_id": "xxxxx",
        //         },
        //     ]
        //
        return this.parseTransactions (response, undefined, since, limit);
    }

    override parseTransaction (transaction: Dict, currency: Currency = undefined): Transaction {
        //
        // fetchDepositsWithdrawals
        //
        //     {
        //         "fee": "0.00000000",
        //         "btc_usd": "0.00",
        //         "id": 1234567894,
        //         "usd": 0,
        //         "btc": 0,
        //         "datetime": "2018-09-08 09:00:31",
        //         "type": "1",
        //         "xrp": "-20.00000000",
        //         "eur": 0,
        //     }
        //
        // fetchWithdrawals
        //
        //     {
        //         "status": 2,
        //         "datetime": "2018-10-17 10:58:13",
        //         "currency": "BTC",
        //         "amount": "0.29669259",
        //         "address": "aaaaa",
        //         "type": 1,
        //         "id": 111111,
        //         "transaction_id": "xxxx",
        //     }
        //
        //     {
        //         "id": 3386432,
        //         "type": 14,
        //         "amount": "863.21332500",
        //         "status": 2,
        //         "address": "rE1sdh25BJQ3qFwngiTBwaq3zPGGYcrjp1?dt=1455",
        //         "currency": "XRP",
        //         "datetime": "2018-01-05 15:27:55",
        //         "transaction_id": "001743B03B0C79BA166A064AC0142917B050347B4CB23BA2AB4B91B3C5608F4C"
        //     }
        //
        const timestamp = this.parse8601 (this.safeString (transaction, 'datetime'));
        const currencyId = this.getCurrencyIdFromTransaction (transaction);
        const code = this.safeCurrencyCode (currencyId, currency);
        const feeCost = this.safeString (transaction, 'fee');
        let feeCurrency: Str = undefined;
        let amount: Str = undefined;
        if ('amount' in transaction) {
            amount = this.safeString (transaction, 'amount');
        } else if (currency !== undefined) {
            amount = this.safeString (transaction, currency['id'], amount);
            feeCurrency = currency['code'];
        } else if ((code !== undefined) && (currencyId !== undefined)) {
            amount = this.safeString (transaction, currencyId, amount);
            feeCurrency = code;
        }
        if (amount !== undefined) {
            // withdrawals have a negative amount
            amount = Precise.stringAbs (amount);
        }
        let status: Str = 'ok';
        if ('status' in transaction) {
            status = this.parseTransactionStatus (this.safeString (transaction, 'status'));
        }
        let type: Str = undefined;
        if ('type' in transaction) {
            // from fetchDepositsWithdrawals
            const rawType = this.safeString (transaction, 'type');
            if (rawType === '0') {
                type = 'deposit';
            } else if (rawType === '1') {
                type = 'withdrawal';
            }
        } else {
            // from fetchWithdrawals
            type = 'withdrawal';
        }
        let tag: Str = undefined;
        let address = this.safeString (transaction, 'address');
        if (address !== undefined) {
            // dt (destination tag) is embedded into the address field
            const addressParts = address.split ('?dt=');
            const numParts = addressParts.length;
            if (numParts > 1) {
                address = addressParts[0];
                tag = addressParts[1];
            }
        }
        let fee: FeeStringInterface = {
            'currency': undefined,
            'cost': undefined,
            'rate': undefined,
        };
        if (feeCost !== undefined) {
            fee = {
                'currency': feeCurrency,
                'cost': feeCost,
                'rate': undefined,
            };
        }
        return {
            'info': transaction,
            'id': this.safeString (transaction, 'id'),
            'txid': this.safeString (transaction, 'transaction_id'),
            'type': type,
            'currency': code,
            'network': undefined,
            'amount': this.parseNumber (amount),
            'status': status,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'address': address,
            'addressFrom': undefined,
            'addressTo': address,
            'tag': tag,
            'tagFrom': undefined,
            'tagTo': tag,
            'updated': undefined,
            'comment': undefined,
            'internal': undefined,
            'fee': fee,
        } as Transaction;
    }

    parseTransactionStatus (status: Str) {
        //
        //   withdrawals:
        //   0 (open), 1 (in process), 2 (finished), 3 (canceled) or 4 (failed).
        //
        const statuses: Dict = {
            '0': 'pending', // Open
            '1': 'pending', // In process
            '2': 'ok', // Finished
            '3': 'canceled', // Canceled
            '4': 'failed', // Failed
        };
        return this.safeString (statuses, status, status);
    }

    override parseOrder (order: Dict, market: Market = undefined): Order {
        //
        //   from fetch order:
        //     { status: "Finished",
        //       "id": 731693945,
        //       "client_order_id": '',
        //       "transactions":
        //       [ { fee: "0.000019",
        //           "price": "0.00015803",
        //           "datetime": "2018-01-07 10:45:34.132551",
        //           "btc": "0.0079015000000000",
        //           "tid": 42777395,
        //           "type": 2,
        //           "xrp": "50.00000000" } ] }
        //
        //   partially filled order:
        //     { "id": 468646390,
        //       "client_order_id": "",
        //       "status": "Canceled",
        //       "transactions": [{
        //           "eth": "0.23000000",
        //           "fee": "0.09",
        //           "tid": 25810126,
        //           "usd": "69.8947000000000000",
        //           "type": 2,
        //           "price": "303.89000000",
        //           "datetime": "2017-11-11 07:22:20.710567"
        //       }]}
        //
        //   from create order response:
        //       {
        //           "price": "0.00008012",
        //           "client_order_id": '',
        //           "currency_pair": "XRP/BTC",
        //           "datetime": "2019-01-31 21:23:36",
        //           "amount": "15.00000000",
        //           "type": "0",
        //           "id": "2814205012"
        //       }
        //
        // cancelOrder
        //
        //    {
        //        "id": 1453282316578816,
        //        "amount": "0.02035278",
        //        "price": "2100.45",
        //        "type": 0,
        //        "market": "BTC/USD"
        //    }
        //
        // editOrder
        //
        //    {
        //        "order_id": 1453282316578816,
        //        "order_type": "0",
        //        "market": "BTC/USD",
        //        "amount": "0.02035278",
        //        "price": "2100.45",
        //        "datetime": "2025-10-17T14:23:01.725000Z",
        //        "orig_order_id": 1453282316578816,
        //        "orig_client_order_id": "my-original-order-123",
        //        "status": "Open"
        //    }
        //
        const id = this.safeString2 (order, 'id', 'order_id');
        const clientOrderId = this.safeString2 (order, 'client_order_id', 'orig_client_order_id');
        let side = this.safeString2 (order, 'type', 'order_type');
        if (side !== undefined) {
            side = (side === '1') ? 'sell' : 'buy';
        }
        // there is no timestamp from fetchOrder
        const timestamp = this.parse8601 (this.safeString (order, 'datetime'));
        const marketId = this.safeStringLower (order, 'currency_pair');
        const symbol = this.safeSymbol (marketId, market, '/');
        const status = this.parseOrderStatus (this.safeString (order, 'status'));
        const amount = this.safeString (order, 'amount');
        const transactions = this.safeValue (order, 'transactions', []);
        const price = this.safeString (order, 'price');
        return this.safeOrder ({
            'id': id,
            'clientOrderId': clientOrderId,
            'datetime': this.iso8601 (timestamp),
            'timestamp': timestamp,
            'lastTradeTimestamp': undefined,
            'status': status,
            'symbol': symbol,
            'type': undefined,
            'timeInForce': undefined,
            'postOnly': undefined,
            'side': side,
            'price': price,
            'triggerPrice': undefined,
            'cost': undefined,
            'amount': amount,
            'filled': undefined,
            'remaining': undefined,
            'trades': transactions,
            'fee': undefined,
            'info': order,
            'average': undefined,
        }, market);
    }

    parseLedgerEntryType (type: any) {
        const types: Dict = {
            '0': 'transaction',
            '1': 'transaction',
            '2': 'trade',
            '14': 'transfer',
        };
        return this.safeString (types, (type as string), type);
    }

    override parseLedgerEntry (item: Dict, currency: Currency = undefined): LedgerEntry {
        //
        //     [
        //         {
        //             "fee": "0.00000000",
        //             "btc_usd": "0.00",
        //             "id": 1234567894,
        //             "usd": 0,
        //             "btc": 0,
        //             "datetime": "2018-09-08 09:00:31",
        //             "type": "1",
        //             "xrp": "-20.00000000",
        //             "eur": 0,
        //         },
        //         {
        //             "fee": "0.00000000",
        //             "btc_usd": "0.00",
        //             "id": 1134567891,
        //             "usd": 0,
        //             "btc": 0,
        //             "datetime": "2018-09-07 18:47:52",
        //             "type": "0",
        //             "xrp": "20.00000000",
        //             "eur": 0,
        //         },
        //     ]
        //
        const type = this.parseLedgerEntryType (this.safeString (item, 'type'));
        if (type === 'trade') {
            const parsedTrade = this.parseTrade (item);
            let market: Market = undefined;
            const keys = Object.keys (item);
            for (let i = 0; i < keys.length; i++) {
                if (keys[i].indexOf ('_') >= 0) {
                    const marketId = keys[i].replace ('_', '');
                    market = this.safeMarket (marketId, market);
                }
            }
            // if the market is still not defined
            // try to deduce it from used keys
            if (market === undefined) {
                market = this.getMarketFromTrade (item);
            }
            const direction = (parsedTrade['side'] === 'buy') ? 'in' : 'out';
            return this.safeLedgerEntry ({
                'info': item,
                'id': parsedTrade['id'],
                'timestamp': parsedTrade['timestamp'],
                'datetime': parsedTrade['datetime'],
                'direction': direction,
                'account': undefined,
                'referenceId': parsedTrade['order'],
                'referenceAccount': undefined,
                'type': type,
                'currency': this.safeString (market, 'base'),
                'amount': parsedTrade['amount'],
                'before': undefined,
                'after': undefined,
                'status': 'ok',
                'fee': parsedTrade['fee'],
            }, currency) as LedgerEntry;
        } else {
            const parsedTransaction = this.parseTransaction (item, currency);
            let direction: Str = undefined;
            if ('amount' in item) {
                const amount = this.safeString (item, 'amount');
                direction = Precise.stringGt (amount, '0') ? 'in' : 'out';
            } else if (('currency' in parsedTransaction) && parsedTransaction['currency'] !== undefined) {
                const currencyCode = this.safeString (parsedTransaction, 'currency');
                currency = this.currency (currencyCode);
                const amount = this.safeString (item, currency['id']);
                direction = Precise.stringGt (amount, '0') ? 'in' : 'out';
            }
            return this.safeLedgerEntry ({
                'info': item,
                'id': parsedTransaction['id'],
                'timestamp': parsedTransaction['timestamp'],
                'datetime': parsedTransaction['datetime'],
                'direction': direction,
                'account': undefined,
                'referenceId': parsedTransaction['txid'],
                'referenceAccount': undefined,
                'type': type,
                'currency': parsedTransaction['currency'],
                'amount': parsedTransaction['amount'],
                'before': undefined,
                'after': undefined,
                'status': parsedTransaction['status'],
                'fee': parsedTransaction['fee'],
            }, currency) as LedgerEntry;
        }
    }

    /**
     * @method
     * @name bitstamp#fetchLedger
     * @description fetch the history of changes, actions done by the user or operations that altered the balance of the user
     * @see https://www.bitstamp.net/api/#tag/Transactions-private/operation/GetUserTransactions
     * @param {string} [code] unified currency code, default is undefined
     * @param {int} [since] timestamp in ms of the earliest ledger entry, default is undefined
     * @param {int} [limit] max number of ledger entries to return, default is undefined
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [ledger structure]{@link https://docs.ccxt.com/?id=ledger-entry-structure}
     */
    override async fetchLedger (code: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<LedgerEntry[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const request: Dict = {};
        if (limit !== undefined) {
            request['limit'] = limit;
        }
        const response = await this.privatePostUserTransactions (this.extend (request, params));
        let currency: Currency = undefined;
        if (code !== undefined) {
            currency = this.currency (code);
        }
        return this.parseLedger (response, currency, since, limit);
    }

    /**
     * @method
     * @name bitstamp#fetchFundingRate
     * @description fetch the current funding rate
     * @see https://www.bitstamp.net/api/#tag/Market-info/operation/GetFundingRate
     * @param {string} symbol unified market symbol
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [funding rate structure]{@link https://docs.ccxt.com/?id=funding-rate-structure}
     */
    override async fetchFundingRate (symbol: string, params = {}): Promise<FundingRate> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request: Dict = {
            'market_symbol': market['id'],
        };
        const response = await this.publicGetFundingRateMarketSymbol (this.extend (request, params));
        //
        //     {
        //         "funding_rate": "0.0024",
        //         "timestamp": "1644406050",
        //         "market": "BTC/USD-PERP",
        //         "next_funding_time": "1644406050"
        //     }
        //
        return this.parseFundingRate (response, market);
    }

    override parseFundingRate (fundingRate: any, market: Market = undefined): FundingRate {
        //
        //     {
        //         "funding_rate": "0.0024",
        //         "timestamp": "1644406050",
        //         "market": "BTC/USD-PERP",
        //         "next_funding_time": "1644406050"
        //     }
        //
        const currentTime = this.safeIntegerProduct (fundingRate, 'timestamp', 1000);
        const nextFundingRateTimestamp = this.safeIntegerProduct (fundingRate, 'next_funding_time', 1000);
        const marketId = this.safeString (fundingRate, 'market');
        return {
            'info': fundingRate,
            'symbol': this.safeSymbol (marketId, market),
            'markPrice': undefined,
            'indexPrice': undefined,
            'interestRate': undefined,
            'estimatedSettlePrice': undefined,
            'timestamp': currentTime,
            'datetime': this.iso8601 (currentTime),
            'previousFundingRate': undefined,
            'nextFundingRate': undefined,
            'previousFundingTimestamp': undefined,
            'nextFundingTimestamp': undefined,
            'previousFundingDatetime': undefined,
            'nextFundingDatetime': undefined,
            'fundingRate': this.safeNumber (fundingRate, 'funding_rate'),
            'fundingTimestamp': nextFundingRateTimestamp,
            'fundingDatetime': this.iso8601 (nextFundingRateTimestamp),
            'interval': undefined,
        } as FundingRate;
    }

    /**
     * @method
     * @name bitstamp#fetchOpenOrders
     * @description fetch all unfilled currently open orders
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/GetAllOpenOrders
     * @see https://www.bitstamp.net/api/#tag/Orders/operation/GetOpenOrdersForMarket
     * @param {string} symbol unified market symbol
     * @param {int} [since] the earliest time in ms to fetch open orders for
     * @param {int} [limit] the maximum number of  open orders structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {Order[]} a list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async fetchOpenOrders (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Order[]> {
        let market: Market = undefined;
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        if (symbol !== undefined) {
            market = this.market (symbol);
        }
        const response = await this.privatePostOpenOrdersAll (params);
        //
        //     [
        //         {
        //             "price": "0.00008012",
        //             "currency_pair": "XRP/BTC",
        //             "client_order_id": '',
        //             "datetime": "2019-01-31 21:23:36",
        //             "amount": "15.00000000",
        //             "type": "0",
        //             "id": "2814205012",
        //         }
        //     ]
        //
        return this.parseOrders (response, market, since, limit, {
            'status': 'open',
            'type': 'limit',
        });
    }

    getCurrencyName (code: any) {
        /**
         * @ignore
         * @method
         * @param {string} code Unified currency code
         * @returns {string} lowercase version of code
         */
        return code.toLowerCase ();
    }

    isFiat (code: any) {
        return code === 'USD' || code === 'EUR' || code === 'GBP';
    }

    /**
     * @method
     * @name bitstamp#fetchDepositAddress
     * @description fetch the deposit address for a currency associated with this account
     * @see https://www.bitstamp.net/api/#tag/Deposits/operation/GetCryptoDepositAddress
     * @param {string} code unified currency code
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} an [address structure]{@link https://docs.ccxt.com/?id=address-structure}
     */
    override async fetchDepositAddress (code: string, params = {}): Promise<DepositAddress> {
        if (this.isFiat (code)) {
            throw new NotSupported (this.id + ' fiat fetchDepositAddress() for ' + code + ' is not supported!');
        }
        const name = this.getCurrencyName (code);
        const method = 'privatePost' + this.capitalize (name) + 'Address';
        const response = await this[method] (params);
        const address = this.safeString (response, 'address');
        const tag = this.safeString2 (response, 'memo_id', 'destination_tag');
        this.checkAddress (address);
        return {
            'info': response,
            'currency': code,
            'network': undefined,
            'address': address,
            'tag': tag,
        } as DepositAddress;
    }

    /**
     * @method
     * @name bitstamp#withdraw
     * @description make a withdrawal
     * @see https://www.bitstamp.net/api/#tag/Withdrawals/operation/RequestFiatWithdrawal
     * @see https://www.bitstamp.net/api/#tag/Withdrawals/operation/RequestCryptoWithdrawal
     * @param {string} code unified currency code
     * @param {float} amount the amount to withdraw
     * @param {string} address the address to withdraw to
     * @param {string} tag
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [transaction structure]{@link https://docs.ccxt.com/?id=transaction-structure}
     */
    override async withdraw (code: string, amount: number, address: string, tag: Str = undefined, params = {}): Promise<Transaction> {
        // For fiat withdrawals please provide all required additional parameters in the 'params'
        // Check https://www.bitstamp.net/api/ under 'Open bank withdrawal' for list and description.
        [ tag, params ] = this.handleWithdrawTagAndParams (tag, params);
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        this.checkAddress (address);
        const request: Dict = {
            'amount': amount,
        };
        let currency: Currency = undefined;
        let method: Str = undefined;
        if (!this.isFiat (code)) {
            const name = this.getCurrencyName (code);
            method = 'privatePost' + this.capitalize (name) + 'Withdrawal';
            if (code === 'XRP') {
                if (tag !== undefined) {
                    request['destination_tag'] = tag;
                }
            } else if (code === 'XLM' || code === 'HBAR') {
                if (tag !== undefined) {
                    request['memo_id'] = tag;
                }
            }
            request['address'] = address;
        } else {
            method = 'privatePostWithdrawalOpen';
            currency = this.currency (code);
            request['iban'] = address;
            request['account_currency'] = currency['id'];
        }
        const response = await this[method] (this.extend (request, params));
        return this.parseTransaction (response, currency);
    }

    /**
     * @method
     * @name bitstamp#transfer
     * @description transfer currency internally between wallets on the same account
     * @see https://www.bitstamp.net/api/#tag/Sub-account/operation/TransferFromMainToSub
     * @see https://www.bitstamp.net/api/#tag/Sub-account/operation/TransferFromSubToMain
     * @param {string} code unified currency code
     * @param {float} amount amount to transfer
     * @param {string} fromAccount account to transfer from
     * @param {string} toAccount account to transfer to
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [transfer structure]{@link https://docs.ccxt.com/?id=transfer-structure}
     */
    override async transfer (code: string, amount: number, fromAccount: string, toAccount: string, params = {}): Promise<TransferEntry> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const currency = this.currency (code);
        const request: Dict = {
            'amount': this.parseToNumeric (this.currencyToPrecision (code, amount)),
            'currency': currency['id'].toUpperCase (),
        };
        let response: NullableDict = undefined;
        if (fromAccount === 'main') {
            request['subAccount'] = toAccount;
            response = await this.privatePostTransferFromMain (this.extend (request, params));
        } else if (toAccount === 'main') {
            request['subAccount'] = fromAccount;
            response = await this.privatePostTransferToMain (this.extend (request, params));
        } else {
            throw new BadRequest (this.id + ' transfer() only supports from or to main');
        }
        //
        //    { status: 'ok' }
        //
        const transfer = this.parseTransfer (response, currency);
        transfer['amount'] = amount;
        transfer['fromAccount'] = fromAccount;
        transfer['toAccount'] = toAccount;
        return transfer;
    }

    override parseTransfer (transfer: any, currency: Currency = undefined) {
        //
        //    { status: 'ok' }
        //
        const status = this.safeString (transfer, 'status');
        if (currency === undefined) {
            throw new ExchangeError (this.id + ' parseTransfer() could not resolve currency');
        }
        const result: Dict = {
            'info': transfer,
            'id': undefined,
            'timestamp': undefined,
            'datetime': undefined,
            'currency': currency['code'],
            'amount': undefined,
            'fromAccount': undefined,
            'toAccount': undefined,
            'status': this.parseTransferStatus (status),
        };
        return result;
    }

    parseTransferStatus (status: Str): Str {
        const statuses: Dict = {
            'ok': 'ok',
            'error': 'failed',
        };
        return this.safeString (statuses, status, status);
    }

    override nonce () {
        return this.milliseconds ();
    }

    override sign (path: any, api: any = 'public', method = 'GET', params = {}, headers: NullableDict = undefined, body: Str = undefined) {
        let url = this.urls['api'][api] + '/';
        url += this.version + '/';
        url += this.implodeParams (path, params);
        const query = this.omit (params, this.extractParams (path));
        if (api === 'public') {
            if (Object.keys (query).length) {
                url += '?' + this.urlencode (query);
            }
        } else {
            this.checkRequiredCredentials ();
            const xAuth = 'BITSTAMP ' + this.apiKey;
            const xAuthNonce = this.uuid ();
            const xAuthTimestamp = this.milliseconds ().toString ();
            const xAuthVersion = 'v2';
            let contentType = '';
            headers = {
                'X-Auth': xAuth,
                'X-Auth-Nonce': xAuthNonce,
                'X-Auth-Timestamp': xAuthTimestamp,
                'X-Auth-Version': xAuthVersion,
            };
            if (method === 'POST') {
                if (Object.keys (query).length) {
                    body = this.urlencode (query);
                    contentType = 'application/x-www-form-urlencoded';
                    headers['Content-Type'] = contentType;
                } else {
                    // sending an empty POST request will trigger
                    // an API0020 error returned by the exchange
                    // therefore for empty requests we send a dummy object
                    // https://github.com/ccxt/ccxt/issues/6846
                    body = this.urlencode ({ 'foo': 'bar' });
                    contentType = 'application/x-www-form-urlencoded';
                    headers['Content-Type'] = contentType;
                }
            }
            const authBody = body ? body : '';
            const auth = xAuth + method + url.replace ('https://', '') + contentType + xAuthNonce + xAuthTimestamp + xAuthVersion + authBody;
            const signature = this.hmac (this.encode (auth), this.encode (this.secret), sha256);
            headers['X-Auth-Signature'] = signature;
        }
        return { 'url': url, 'method': method, 'body': body, 'headers': headers };
    }

    override handleErrors (httpCode: int, reason: string, url: string, method: string, headers: Dict, body: string, response: any, requestHeaders: any, requestBody: any) {
        if (response === undefined) {
            return undefined;
        }
        //
        //     {"error": "No permission found"} // fetchDepositAddress returns this on apiKeys that don't have the permission required
        //     {"status": "error", "reason": {"__all__": ["Minimum order size is 5.0 EUR."]}}
        //     reuse of a nonce gives: { status: 'error', reason: 'Invalid nonce', code: 'API0004' }
        //
        const status = this.safeString (response, 'status');
        const error = this.safeValue (response, 'error');
        if ((status === 'error') || (error !== undefined)) {
            let errors: string[] = [];
            if (typeof error === 'string') {
                errors.push (error);
            } else if (error !== undefined) {
                const keys = Object.keys (error);
                for (let i = 0; i < keys.length; i++) {
                    const key = keys[i];
                    const value = this.safeValue (error, key);
                    if (Array.isArray (value)) {
                        errors = this.arrayConcat (errors, value);
                    } else {
                        errors.push (value);
                    }
                }
            }
            const reasonInner = this.safeValue (response, 'reason', {});
            if (typeof reasonInner === 'string') {
                errors.push (reasonInner);
            } else {
                const all = this.safeValue (reasonInner, '__all__', []);
                for (let i = 0; i < all.length; i++) {
                    errors.push (all[i]);
                }
            }
            const code = this.safeString (response, 'code');
            if (code === 'API0005') {
                throw new AuthenticationError (this.id + ' invalid signature, use the uid for the main account if you have subaccounts');
            }
            const feedback = this.id + ' ' + body;
            for (let i = 0; i < errors.length; i++) {
                const value = errors[i];
                this.throwExactlyMatchedException (this.exceptions['exact'], value, feedback);
                this.throwBroadlyMatchedException (this.exceptions['broad'], value, feedback);
            }
            throw new ExchangeError (feedback);
        }
        return undefined;
    }
}
